'use client';
import { useEffect, useState } from 'react';

export type PortalRole = 'admin' | 'mentor' | 'member';

export interface PortalIdentity {
  role: PortalRole;
  name: string;
  initials: string;
  subtitle: string;
  roleLabel: string;
  home: string;
  loginPath: string;
}

export const portalIdentities: Record<PortalRole, PortalIdentity> = {
  admin: {
    role: 'admin',
    name: 'Superuser',
    initials: 'SU',
    subtitle: 'Care Business School',
    roleLabel: 'Superuser Console',
    home: '/admin-console',
    loginPath: '/portal/admin',
  },
  mentor: {
    role: 'mentor',
    name: 'Mentor',
    initials: 'M',
    subtitle: 'Care Business School',
    roleLabel: 'Mentor Portal',
    home: '/mentorship-sessions',
    loginPath: '/portal/mentor',
  },
  member: {
    role: 'member',
    name: 'Member',
    initials: 'M',
    subtitle: 'Care Business School',
    roleLabel: 'Member Portal',
    home: '/dashboard',
    loginPath: '/portal/member',
  },
};

const STORAGE_KEY = 'cbs-portal-role';

export function setStoredRole(role: PortalRole) {
  try {
    window.localStorage.setItem(STORAGE_KEY, role);
  } catch {
    // storage unavailable
  }
}

export function clearStoredRole() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage unavailable
  }
}

export function signOutAndClear() {
  clearStoredRole();
  try {
    // Lazy import keeps this helper safe to call from any client component
    import('@/lib/supabase/client').then(({ createClient }) => createClient().auth.signOut());
  } catch {
    // ignore
  }
}

export const SET_PASSWORD_PATH = '/set-password';

export function mustSetPassword(user: any): boolean {
  return user?.user_metadata?.must_set_password === true;
}

export async function signInWithRole(
  signIn: (email: string, password: string) => Promise<any>,
  getRole: (userId: string) => Promise<PortalRole | null>,
  email: string,
  password: string,
  expectedRole?: PortalRole
): Promise<PortalRole> {
  const data = await signIn(email, password);
  const userId: string | undefined = data?.user?.id;
  const role = userId ? await getRole(userId) : null;
  if (!role) {
    throw new Error('Your account has no portal role assigned. Please contact the CBS team.');
  }
  if (expectedRole && role !== expectedRole && !(expectedRole === 'admin' && role === 'mentor')) {
    throw new Error(`This account is not a ${expectedRole === 'admin' ? 'superuser' : expectedRole} account. Use the correct portal tab.`);
  }
  setStoredRole(role);
  return role;
}

export async function resolvePostLoginPath(role: PortalRole): Promise<string> {
  try {
    const { createClient } = await import('@/lib/supabase/client');
    const { data } = await createClient().auth.getUser();
    if (mustSetPassword(data?.user)) return SET_PASSWORD_PATH;
  } catch {
    // fall through to the role home
  }
  return portalIdentities[role].home;
}

export async function fetchProfileRole(userId: string): Promise<PortalRole | null> {
  const { createClient } = await import('@/lib/supabase/client');
  const { data, error } = await createClient().from('user_profiles').select('role').eq('id', userId).maybeSingle();
  if (error) throw error;
  const role = data?.role;
  return role === 'admin' || role === 'mentor' || role === 'member' ? role : null;
}

export function useRole(): PortalRole {
  const [role, setRole] = useState<PortalRole>('member');
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === 'admin' || stored === 'mentor' || stored === 'member') {
        setRole(stored);
      }
    } catch {
      // storage unavailable
    }
  }, []);
  return role;
}
